import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { AddProductFromMapDialog } from "../../admin/products/map/AddProductFromMapDialog";
import type { ProductResponse } from "../../admin/products/productsApi";
import type { Currency } from "../../data/BaseEntity";
import ProductOptionsWithPrice from "../../sharedComponents/products/ProductOptionsWithPrice";
import ProductPrice from "../../sharedComponents/products/ProductPrice";
import ProductSelect from "../../sharedComponents/products/ProductSelect";
import CalculationProductName from "../calculationProduct/CalculationProductName";
import type { CalculationProduct, CalculationProductOption } from "../calculationTypes";
import ProductsPriceSummary from "./productsPriceSummary/ProductsPriceSummary";
import type { CalculationProductWithPrices } from "./useProductsWithPrices";

interface ProductRowProps {
  readonly rowIndex: number;
  readonly value: CalculationProductWithPrices & { error?: boolean };
  readonly products: ProductResponse[];
  readonly calculationCurrency: Currency | undefined;
  readonly calculationMargin: number | undefined;
  readonly totalParticipantsNumber: number;
  readonly onChange: (value: CalculationProduct, idx: number) => void;
  readonly onDelete: (idx: number) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

const datePickerSlotProps = {
  htmlInput: { "aria-label": "Data", role: "datePicker" },
} as const;

const ProductRow: FC<ProductRowProps> = ({
  rowIndex,
  value,
  products,
  calculationCurrency,
  calculationMargin,
  totalParticipantsNumber,
  onChange,
  onDelete,
  onReplaceProduct,
}) => {
  const product = products.find((p) => p.id === value.id);
  const productOptions = product?.options ?? [];
  const onReplace = (replacement: CalculationProduct) => onReplaceProduct(value.id, replacement);

  const handleChange = (change: Partial<CalculationProduct>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { error, price, ...other } = value;
    onChange({ ...other, ...change } as CalculationProduct, rowIndex);
  };

  return (
    <TableRow>
      <TableCell>
        <CalculationProductName
          selectedProduct={value as CalculationProduct}
          productDetails={product}
          calculationMargin={calculationMargin}
          calculationCurrency={calculationCurrency}
          participantsNumber={totalParticipantsNumber}
          onReplace={onReplace}
        >
          <ProductSelect
            value={value.id}
            products={products}
            fullWidth
            minWidth="280px"
            size="small"
            error={value.error}
            onChange={(id) => handleChange({ id })}
          />
        </CalculationProductName>
      </TableCell>
      <TableCell>
        <IconButton color="secondary" onClick={() => onDelete(rowIndex)} data-testid="delete-product">
          <DeleteIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          size="small"
          label="Data"
          slotProps={datePickerSlotProps}
          value={value.date}
          type="date"
          onChange={(e) => handleChange({ date: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <ProductOptionsWithPrice
          productId={value.id}
          productDate={value.date}
          selectedOptions={value.options}
          availableOptions={productOptions.map((o) => ({ ...o, info: o.info.polish }))}
          onChange={(options) => handleChange({ options: options as CalculationProductOption[] })}
        />
      </TableCell>
      <TableCell width="1%" sx={{ whiteSpace: "nowrap" }}>
        <ProductPrice price={value.price} currency={calculationCurrency} />
      </TableCell>
    </TableRow>
  );
};

interface Props {
  readonly selectedProducts: (CalculationProductWithPrices & { error?: boolean })[];
  readonly availableProducts: ProductResponse[];
  readonly calculationCurrency: Currency | undefined;
  readonly totalParticipantsNumber: number;
  readonly paidParticipantsNumber: number;
  readonly margin: number | undefined;
  readonly onChange: (value: CalculationProduct, idx: number) => void;
  readonly onAdd: (product?: { id: string; date: string }) => void;
  readonly onDelete: (idx: number) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

const CustomCalculationProducts: FC<Props> = ({
  selectedProducts,
  availableProducts,
  calculationCurrency,
  totalParticipantsNumber,
  paidParticipantsNumber,
  margin,
  onChange,
  onAdd,
  onDelete,
  onReplaceProduct,
}) => {
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <>
      <Table data-testid="calculation-products">
        <TableHead>
          <TableRow>
            <TableCell>Produkt</TableCell>
            <TableCell></TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Opcje</TableCell>
            <TableCell width="1%" sx={{ whiteSpace: "nowrap" }}>
              Cena
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedProducts.map((p, idx) => (
            <ProductRow
              key={`${p.id}#${p.date}`}
              rowIndex={idx}
              value={p}
              products={availableProducts}
              calculationCurrency={calculationCurrency}
              calculationMargin={margin}
              totalParticipantsNumber={totalParticipantsNumber}
              onChange={onChange}
              onDelete={onDelete}
              onReplaceProduct={onReplaceProduct}
            />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell>
              <Button onClick={() => onAdd()}>Dodaj produkt</Button>
              <Button onClick={() => setMapOpen(true)}>Dodaj z mapy</Button>
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell colSpan={2}>
              <ProductsPriceSummary
                calculationProducts={selectedProducts}
                availableProducts={availableProducts}
                currency={calculationCurrency}
                margin={margin ?? 0}
                numberOfPeople={paidParticipantsNumber}
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <AddProductFromMapDialog
        open={mapOpen}
        availableProducts={availableProducts}
        onClose={() => setMapOpen(false)}
        onAddProduct={(product, date) => {
          onAdd({ id: product.id, date: date ?? "" });
          setMapOpen(false);
        }}
      />
    </>
  );
};

export default CustomCalculationProducts;
